import { ReactNode, useEffect, useRef, useState, useCallback, useContext } from "react"
import { Alert } from "flowbite-react"
import { HiInformationCircle, HiCheckCircle, HiExclamation, HiXCircle } from "react-icons/hi"
import { AlertContext } from "./contexts"

export interface GlobalAlertType {
  id: string
  color?: "info" | "success" | "warning" | "failure" | "error"
  type?: string
  onDismiss?: () => void
  rounded?: boolean
  withBorderAccent?: boolean
  additionalContent?: ReactNode
  className?: string
  children: ReactNode
  timeoutMs?: number
  message?: string
  createdAt?: number
}

export interface AlertContextType {
  alerts: GlobalAlertType[]
  addAlert: (alert: GlobalAlertType) => void
  removeAlert: (id: string) => void
}

export function UseAlertStore() {
  const [alerts, setAlerts] = useState<GlobalAlertType[]>([])

  const addAlert = useCallback(
    (alert: GlobalAlertType) => {
      setAlerts((prevAlerts) => {
        if (prevAlerts.some((existingAlert) => existingAlert.id === alert.id)) {
          return prevAlerts
        }
        return [...prevAlerts, { ...alert, createdAt: Date.now() }]
      })
    },
    [setAlerts]
  )

  const removeAlert = useCallback(
    (id: string) => {
      console.log("removeAlert", id, alerts)
      setAlerts((prevAlerts) => prevAlerts.filter((alert) => alert.id !== id))
    },
    [setAlerts]
  )

  return { alerts, addAlert, removeAlert }
}

/**
 * GlobalAlert renders all global alerts from AlertContext using Flowbite's Alert component.
 * Supports all Flowbite Alert styles and custom actions.
 */
export default function GlobalAlert() {
  const { alerts, removeAlert } = useContext(AlertContext)
  const timeouts = useRef<{ [id: string]: ReturnType<typeof setTimeout> }>({})

  useEffect(() => {
    alerts.forEach((alert: GlobalAlertType) => {
      const id = alert.id
      if (alert.timeoutMs && !timeouts.current[id]) {
        timeouts.current[id] = setTimeout(() => {
          removeAlert(id)
          delete timeouts.current[id]
        }, alert.timeoutMs)
      }
    })
    // Cleanup on unmount
    return () => {
      Object.values(timeouts.current).forEach(clearTimeout)
      timeouts.current = {}
    }
  }, [alerts, removeAlert])

  const iconMap: any = {
    info: HiInformationCircle,
    success: HiCheckCircle,
    warning: HiExclamation,
    failure: HiXCircle,
    error: HiXCircle
  }

  // Sort alerts by createdAt timestamp (oldest first)
  const sortedAlerts = [...alerts].sort((a, b) => {
    return (a.createdAt || 0) - (b.createdAt || 0)
  })

  return (
    <div className="pt-safe fixed top-0 left-0 w-full z-50 flex flex-col items-center gap-2" id="global-alerts">
      {sortedAlerts.map((alert: GlobalAlertType) => (
        <div key={alert.id} className="flex w-full justify-center">
          <Alert
            color={alert.color || "info"}
            icon={iconMap[alert.color || alert.type || "info"]}
            onDismiss={() => {
              removeAlert(alert.id)
              if (alert.onDismiss) {
                alert.onDismiss()
              }
            }}
            rounded={alert.rounded}
            withBorderAccent={alert.withBorderAccent}
            additionalContent={alert.children}
            className={alert.className}
          >
            {alert.message}
          </Alert>
        </div>
      ))}
    </div>
  )
}
